import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, FileText, Phone, Calendar, ArrowRight, Check } from 'lucide-react';
import { FastTrackForm } from './FastTrackForm';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function ThankYouPage() {
  const [showFastTrack, setShowFastTrack] = useState(false);
  const [fastTrackComplete, setFastTrackComplete] = useState(false);
  // Check if this was a recent resubmission (within 10 minutes)
  const checkRecentSubmission = () => {
    const keys = Object.keys(sessionStorage);
    const emailJsKeys = keys.filter(key => key.startsWith('dermaclinic_emailjs_sent_'));
    if (emailJsKeys.length > 0) {
      const lastKey = emailJsKeys[emailJsKeys.length - 1];
      const lastSubmitTime = sessionStorage.getItem(lastKey);
      if (lastSubmitTime) {
        const timeSinceLastSubmit = Date.now() - parseInt(lastSubmitTime);
        const tenMinutes = 10 * 60 * 1000;
        return timeSinceLastSubmit < tenMinutes;
      }
    }
    return false;
  };
  const isRecentResubmission = checkRecentSubmission();
  const handleAccelerateClick = () => {
    setShowFastTrack(true);
  };
  const handleFastTrackSubmit = () => {
    setFastTrackComplete(true);
    setShowFastTrack(false);
  };
  const handleSkip = () => {
    setShowFastTrack(false);
  };
  return <div className="min-h-screen w-full bg-white" data-id="element-366">
      {/* Header with Logo */}
      <header className="border-b border-[#E6D3B3]/30 bg-white" data-id="element-367">
        <div className="max-w-7xl mx-auto px-4 py-4" data-id="element-368">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-10 md:h-12 w-auto" data-id="element-369" />
        </div>
      </header>

      <div className="px-4 py-20" data-id="element-370">
        <div className="max-w-4xl mx-auto" data-id="element-371">
          <AnimatePresence mode="wait" data-id="element-372">
            {!showFastTrack ? <motion.div key="confirmation" initial="hidden" animate="visible" exit={{
            opacity: 0,
            y: -20
          }} variants={staggerContainer} data-id="element-373">
                {/* PRIMARY CONFIRMATION MESSAGE */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-374">
                  <div className="text-center max-w-2xl mx-auto" data-id="element-375">
                    <div className="w-20 h-20 rounded-full bg-[#C9A961]/10 border-2 border-[#C9A961] flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-376">
                      <CheckCircle className="w-10 h-10 text-[#C9A961]" strokeWidth={1.75} data-id="element-377" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#2E2E2E] mb-4 leading-tight" data-id="element-378">
                      ¡Gracias por tu Solicitud!
                    </h1>
                    <p className="text-lg text-[#555555] leading-relaxed mb-8" data-id="element-379">
                      Tu consulta ha sido recibida exitosamente. Nuestro equipo
                      se pondrá en contacto contigo muy pronto para confirmar tu
                      cita.
                    </p>
                  </div>
                </motion.div>

                {/* CONFIRMATION STATUS BOX */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-380">
                  <div className="flex items-center justify-center gap-3 p-6 bg-[#F7F4EF] rounded-xl border-2 border-[#E6D3B3] shadow-sm" data-id="element-381">
                    <CheckCircle className="w-6 h-6 text-[#C9A961] flex-shrink-0" strokeWidth={1.75} data-id="element-382" />
                    <div className="text-left" data-id="element-383">
                      <p className="text-base font-semibold text-[#2E2E2E] mb-1" data-id="element-384">
                        Solicitud Recibida
                      </p>
                      <p className="text-sm text-[#555555]" data-id="element-385">
                        {isRecentResubmission ? 'Ya recibimos tu solicitud recientemente. Nuestro equipo se pondrá en contacto contigo muy pronto.' : 'Te contactaremos por llamada o WhatsApp en menos de 24 horas para coordinar tu consulta gratuita.'}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* NEXT STEPS SECTION */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-386">
                  <div className="bg-white rounded-2xl p-8 md:p-10 border-2 border-[#E6D3B3] shadow-lg" data-id="element-387">
                    <h2 className="text-2xl font-bold text-[#2E2E2E] mb-6 text-center" data-id="element-388">
                      Qué Sigue Ahora
                    </h2>

                    <div className="space-y-4 mb-6" data-id="element-389">
                      <div className="flex items-start gap-3" data-id="element-390">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-391">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-392" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-393">
                          Un especialista revisa tu solicitud y prepara tu
                          consulta personalizada.
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-394">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-395">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-396" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-397">
                          Te contactamos por llamada o WhatsApp en menos de 24
                          horas.
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-398">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-399">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-400" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-401">
                          Confirmamos tu consulta gratuita y resolvemos todas
                          tus preguntas.
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-[#888888] text-center leading-relaxed" data-id="element-402">
                      Si no ves nuestro mensaje, revisa llamadas perdidas o
                      mensajes de WhatsApp.
                    </p>
                  </div>
                </motion.div>

                {/* WHAT TO EXPECT SECTION */}
                <motion.div variants={fadeInUp} className="bg-[#F7F4EF] rounded-2xl p-8 md:p-10 mb-12 border-2 border-[#E6D3B3]" data-id="element-403">
                  <h2 className="text-2xl font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-404">
                    Qué Esperar en tu Consulta
                  </h2>

                  <div className="space-y-6" data-id="element-405">
                    {[{
                  icon: FileText,
                  title: 'Evaluación Médica Completa',
                  desc: 'Nuestros especialistas revisarán tu caso y discutirán tus objetivos estéticos.'
                }, {
                  icon: Phone,
                  title: 'Plan Personalizado',
                  desc: 'Recibirás un plan de tratamiento diseñado específicamente para ti.'
                }, {
                  icon: Calendar,
                  title: 'Precios Transparentes',
                  desc: 'Te proporcionaremos costos claros, opciones de financiamiento y próximos pasos.'
                }].map((item, idx) => <div key={idx} className="flex items-start space-x-4" data-id="element-406">
                        <div className="w-12 h-12 rounded-full bg-white border-2 border-[#E6D3B3] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-407">
                          <item.icon className="w-6 h-6 text-[#C9A961]" strokeWidth={1.75} data-id="element-408" />
                        </div>
                        <div className="flex-1" data-id="element-409">
                          <h3 className="font-semibold text-[#2E2E2E] mb-1 text-lg" data-id="element-410">
                            {item.title}
                          </h3>
                          <p className="text-sm text-[#555555] leading-relaxed" data-id="element-411">
                            {item.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </motion.div>

                {/* CONTACT INFO */}
                <motion.div variants={fadeInUp} className="text-center" data-id="element-412">
                  <p className="text-sm text-[#888888] mb-2" data-id="element-413">
                    ¿Tienes preguntas urgentes?
                  </p>
                  <p className="text-base text-[#2E2E2E] font-medium" data-id="element-414">
                    Nuestro equipo está disponible para ayudarte
                  </p>
                </motion.div>
              </motion.div> : <motion.div key="fasttrack" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-415">
                <FastTrackForm onSubmit={handleFastTrackSubmit} onSkip={handleSkip} data-id="element-416" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#F7F4EF] border-t border-[#E6D3B3]/30" data-id="element-417">
        <div className="max-w-7xl mx-auto text-center" data-id="element-418">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 w-auto mx-auto mb-4 opacity-60" data-id="element-419" />
          <p className="text-xs text-[#888888]" data-id="element-420">
            © 2024 DermaClinic Center. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}